import { useState } from "react";
import { useResetPassword } from "ra-supabase-core";
import { Form, required, useNotify, useRedirect, useTranslate } from "ra-core";
import type { FieldValues, SubmitHandler } from "react-hook-form";
import { useLocation } from "react-router";

import { Layout } from "@/components/supabase/layout";
import { TextInput } from "@/components/admin/text-input";
import { Button } from "@/components/ui/button";

import { ENLACE_NO_VALIDO, urlDeVuelta } from "./enlaceCorreo";

/**
 * «¿Has olvidado tu contraseña?» (copia adaptada de components/supabase/forgot-password-page.tsx
 * de Atomic, 09/10/2026):
 * - pide el correo con la dirección de vuelta de la app, sin barra final (ver enlaceCorreo.ts);
 * - si se llega desde un enlace de correo que ya no sirve (usado o caducado), lo explica
 *   arriba en vez de mandar al inicio de sesión sin más (auth-callback.html añade ?enlace=no-valido).
 */
export const PaginaOlvido = () => {
  const [cargando, setCargando] = useState(false);
  const notify = useNotify();
  const redirect = useRedirect();
  const translate = useTranslate();
  const location = useLocation();
  const enlaceNoValido =
    new URLSearchParams(location.search).get("enlace") === ENLACE_NO_VALIDO;
  const [, { mutateAsync: pedirEnlace }] = useResetPassword({
    onSuccess: () => {
      redirect("/login?passwordRecoveryEmailSent=1");
    },
    onError: () => undefined,
  });

  const enviar = async (valores: { email: string }) => {
    try {
      setCargando(true);
      await pedirEnlace({
        email: valores.email,
        redirectTo: urlDeVuelta(window.location.href),
      });
    } catch (error: unknown) {
      const mensaje =
        typeof error === "string"
          ? error
          : (error as { message?: string } | undefined)?.message;
      notify(mensaje || "ra.auth.sign_in_error", {
        type: "warning",
        messageArgs: { _: mensaje },
      });
    } finally {
      setCargando(false);
    }
  };

  return (
    <Layout>
      {enlaceNoValido ? (
        <div
          role="alert"
          data-testid="enlace-no-valido"
          className="rounded-md border border-amber-600/50 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-200 space-y-1"
        >
          <p className="font-medium">
            {translate("nevora.login.enlace_no_valido")}
          </p>
          <p>{translate("nevora.login.enlace_no_valido_ayuda")}</p>
        </div>
      ) : null}
      <div className="flex flex-col space-y-2 text-center">
        <h1 className="text-2xl font-semibold tracking-tight">
          {translate("ra-supabase.reset_password.forgot_password")}
        </h1>
        <p>{translate("ra-supabase.reset_password.forgot_password_details")}</p>
      </div>
      <Form
        className="space-y-8"
        onSubmit={enviar as SubmitHandler<FieldValues>}
      >
        <TextInput
          source="email"
          label={translate("ra.auth.email")}
          autoComplete="email"
          validate={required()}
        />
        <Button type="submit" className="cursor-pointer" disabled={cargando}>
          {translate("crm.action.reset_password")}
        </Button>
      </Form>
    </Layout>
  );
};

// Misma ruta que la de Atomic: authProvider.checkAuth la deja pasar sin sesión.
PaginaOlvido.path = "forgot-password";
